import type { TFunction } from 'i18next';

import { PHONE_DIGITS } from '../domain/identifier';
import type { IdentifierProblem } from '../domain/identifier';
import type { Refusal } from '../domain/refusal';
import type { AlertChannel, IdentifierKind, PasscodeChannel } from '../types/domain.types';

import { refusalText } from './refusalText';

export const identifierLabel = (t: TFunction): string => t('passcodeAuth.login.identifier', 'Phone or email');

export const identifierPlaceholder = (t: TFunction): string =>
  t('passcodeAuth.login.identifierPlaceholder', '98765 43210 or you@company.com');

export const identifierHint = (t: TFunction, kind: IdentifierKind | null, len: number): string | null => {
  switch (kind) {
    case 'PHONE':
      return t('passcodeAuth.hint.phoneEither', 'We’ll send a {{len}}-digit code on WhatsApp or SMS. Your alerts will come the same way.', { len });
    case 'EMAIL':
      return t('passcodeAuth.hint.emailLen', 'We’ll send a {{len}}-digit code to this email.', { len });
    case null:
      return null;
  }
};

export const unverifiedHint = (t: TFunction, kind: IdentifierKind, len: number): string => {
  switch (kind) {
    case 'PHONE':
      return t('passcodeAuth.hint.phoneUnverifiedEither', 'This number isn’t verified yet. Typing the {{len}}-digit code we send verifies it too.', { len });
    case 'EMAIL':
      return t(
        'passcodeAuth.hint.emailUnverified',
        'This email isn’t verified yet. We’ll send a {{len}}-digit code to it; typing it verifies the email too.',
        { len },
      );
  }
};

export const identifierProblemText = (t: TFunction, kind: IdentifierKind | null): string => {
  switch (kind) {
    case 'PHONE':
      return t('passcodeAuth.login.phoneProblem', 'Enter your {{digits}}-digit mobile number.', { digits: PHONE_DIGITS });
    case 'EMAIL':
      return t('passcodeAuth.login.emailProblem', 'Enter an email address like you@company.com.');
    case null:
      return t('passcodeAuth.login.identifierProblem', 'Enter your mobile number or email.');
  }
};

export const identifierRefusalText = (t: TFunction, refusal: Refusal, kind: IdentifierKind | null): string => {
  if (refusal.code !== 'UNKNOWN_IDENTIFIER') return refusalText(t, refusal);
  if (kind === 'EMAIL') return t('passcodeAuth.refusal.unknownEmail', 'We don’t recognise this email. Ask your site admin to invite you.');
  return t('passcodeAuth.refusal.unknownPhone', 'We don’t recognise this number. Ask your site admin to invite you.');
};

export interface IdentifierHelpInput {
  readonly kind: IdentifierKind | null;
  readonly refusal: Refusal | null;
  readonly problem: IdentifierProblem | null;
  readonly unverifiedKind: IdentifierKind | null;
  readonly codeLength: number | null;
}

export interface IdentifierHelp {
  readonly text: string | null;
  readonly error: boolean;
}

export const identifierHelp = (t: TFunction, { kind, refusal, problem, unverifiedKind, codeLength }: IdentifierHelpInput): IdentifierHelp => {
  if (refusal !== null) return { text: identifierRefusalText(t, refusal, kind), error: true };
  if (problem !== null) return { text: identifierProblemText(t, kind), error: true };
  if (codeLength === null) return { text: null, error: false };
  if (unverifiedKind !== null) return { text: unverifiedHint(t, unverifiedKind, codeLength), error: false };
  return { text: identifierHint(t, kind, codeLength), error: false };
};

export const channelName = (t: TFunction, channel: PasscodeChannel): string => {
  switch (channel) {
    case 'WHATSAPP':
      return t('passcodeAuth.channel.whatsapp', 'WhatsApp');
    case 'SMS':
      return t('passcodeAuth.channel.sms', 'SMS');
    case 'EMAIL':
      return t('passcodeAuth.channel.email', 'email');
  }
};

export const verifiesText = (t: TFunction, sentOn: PasscodeChannel): string =>
  sentOn === 'EMAIL'
    ? t('passcodeAuth.code.verifiesEmail', 'This email isn’t verified yet. Typing the code verifies it.')
    : t('passcodeAuth.code.verifiesPhone', 'This number isn’t verified yet. Typing the code verifies it.');

export const changeTargetLabel = (t: TFunction, sentOn: PasscodeChannel): string =>
  sentOn === 'EMAIL' ? t('passcodeAuth.code.changeEmail', 'Change email') : t('passcodeAuth.code.changeNumber', 'Change number');

export const firstChannelLabel = (t: TFunction, channel: AlertChannel): string => {
  switch (channel) {
    case 'WHATSAPP':
      return t('passcodeAuth.login.sendWhatsapp', 'Send code on WhatsApp');
    case 'SMS':
      return t('passcodeAuth.login.sendSmsFirst', 'Send code by SMS');
  }
};

export const otherChannelLabel = (t: TFunction, channel: AlertChannel): string => {
  switch (channel) {
    case 'WHATSAPP':
      return t('passcodeAuth.login.sendWhatsappInstead', 'Prefer WhatsApp? Send code on WhatsApp');
    case 'SMS':
      return t('passcodeAuth.login.sendSms', 'Prefer SMS? Send code by SMS');
  }
};
